import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  VibesitesImage,
  getMediaUrl,
  parseStorageKey,
  videoThumbStorageKey,
} from '@vibesites/ui-primitives';
import { btnPrimary, linkTarget } from '../utils/content';

const bgDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    video: { label: 'Video', type: 'video', defaultField: 'property_featured_video' },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Hero Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Check Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function Hero() {
  const title = useContentToken('hero.title');
  const bg = useSlotGroup('hero.background', bgDef);
  const cta = useSlotGroup('hero.cta', ctaDef);

  const showVideo = !!bg.video;
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster = (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bg.image || '';

  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <section className="relative isolate flex h-[72svh] min-h-[480px] items-end overflow-hidden bg-secondary md:h-[80vh] md:min-h-[560px] lg:max-h-[920px]">
      <EditableSlotGroup groupPath="hero.background" definition={bgDef}>
        <div className="js-parallax absolute inset-0 -z-10">
          {showVideo ? (
            <video
              ref={videoRef}
              className="js-kenburns h-full w-full object-cover"
              src={bg.video || undefined}
              poster={poster}
              autoPlay={!reducedMotion}
              muted
              loop
              playsInline
              aria-hidden
            />
          ) : (
            <VibesitesImage url={bg.image || ''} alt="" priority sizes="100vw" className="js-kenburns h-full w-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />
        </div>
      </EditableSlotGroup>

      <div className="relative mx-auto flex w-full max-w-5xl flex-col items-center px-6 pb-14 text-center text-white sm:pb-16 md:pb-[9vh]">
        <span aria-hidden className="js-grow-x js-d2 mb-5 block h-[3px] w-24 bg-white/80" />
        <EditableToken
          path="hero.title"
          as="h1"
          className="js-enter js-d1 js-heading max-w-4xl text-balance text-[34px] drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[46px] lg:text-[58px]"
        >
          {title}
        </EditableToken>
        <RichTextContent
          path="hero.subtitle"
          as="div"
          className="js-enter js-d3 mt-3 font-body text-[20px] font-normal tracking-wide drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] sm:text-[26px]"
        />
        <EditableSlotGroup groupPath="hero.cta" definition={ctaDef} className="js-enter js-d5">
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a href={cta.url || '/floor-plans'} target={linkTarget(cta.url, cta._target)} className={`${btnPrimary} px-8`}>
              {cta.label || 'Check Availability'}
            </a>
            <Dialog>
              <DialogTrigger asChild>
                <Button type="button" variant="secondary" size="sm">
                  Test Dialog
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Radix Dialog</DialogTitle>
                  <DialogDescription>Test dialog from @vibesites/ui-primitives.</DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
            <Sheet>
              <SheetTrigger asChild>
                <Button type="button" variant="secondary" size="sm">
                  Test Sheet
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <SheetHeader>
                  <SheetTitle>Radix Sheet</SheetTitle>
                  <SheetDescription>Test sheet from @vibesites/ui-primitives.</SheetDescription>
                </SheetHeader>
              </SheetContent>
            </Sheet>
          </div>
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Home Hero',
  tokens: {
    'hero.title': {
      label: 'Headline',
      type: 'text',
      defaultField: 'property_home_hero_title',
      defaultValue: 'Local Lifestyle. City Comforts.',
      suggestedFields: ['property_home_hero_title', 'property_name'],
      generator: {
        prompt: 'A short two-beat hero headline for this apartment community, in the style "Local Lifestyle. City Comforts."',
        context: 'Full-bleed home hero, uppercase, centered over the featured photo. Sits above a "Apartments in City, State" subtitle.',
        length: '3–6 words',
      },
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
  },
  slotGroups: {
    'hero.background': bgDef,
    'hero.cta': ctaDef,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
